import { useContext, useEffect } from 'react';
import { PersonDataContext } from '../../../../../../contexts/person-data-context';
import { get } from '../../../../../../utils';
import { IGeneratedCase } from '../../..';
import { Document } from '../../../../../../data-models';
import { LetterItem } from './letter-item';
import { MultipleCardContainer } from '../../../components/tw-components';
import { Link } from 'react-router-dom';

type Props = {
  claim: IGeneratedCase;
  headerLink?: string;
  setLetterCount: React.Dispatch<React.SetStateAction<number | null>>;
};
export const OverviewLetters = ({
  claim,
  headerLink,
  setLetterCount,
}: Props) => {
  const { personData } = useContext(PersonDataContext);

  const letters: Document[] = get(personData, 'documents.letters', [])
    .filter(
      (letter: Document) =>
        claim.claims.find(
          (c) => c.caseId === letter.claimId || c.caseId === letter.leaveId,
        ) ||
        claim.leaves.find(
          (l) => l.caseId === letter.claimId || l.caseId === letter.leaveId,
        ) ||
        claim.accommodations.find(
          (a) => a.caseId === letter.claimId || a.caseId === letter.leaveId,
        ),
    )
    .sort((a: Document, b: Document) => {
      return (b.receivedDate ?? '').localeCompare(a.receivedDate ?? '');
    });

  useEffect(() => {
    const count = letters?.length === 0 ? null : letters.length;
    setLetterCount(count);
  }, [letters.length, setLetterCount]);

  const visibleLetters = letters.slice(0, 5);
  const remainingLettersCount = Math.max(letters.length - 5, 0);

  return (
    <MultipleCardContainer id="letters-card">
      {letters && letters?.length > 0 ? (
        <div className="tw-flex tw-flex-col tw-w-full tw-overflow-hidden tw-rounded-md tw-bg-white">
          <div className="tw-divide-y tw-divide-unm-interactive01-400">
            {visibleLetters.map((letter, index) => (
              <LetterItem
                item={letter}
                index={index}
                key={`${letter.documentKey ?? 'documentKey unknown'}-${index}`}
              />
            ))}
          </div>
          {remainingLettersCount > 0 && headerLink && (
            <div className="tw-p-4 tw-flex tw-justify-end tw-border-t tw-border-solid tw-border-0 tw-border-unm-border1">
              <Link
                to={headerLink}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-text-unm-interactive01-400 tw-no-underline"
              >
                <span>View more letters ({remainingLettersCount})</span>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  className="tw-h-4 tw-w-4 tw-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 3.5 10.5 8 5 12.5" />
                </svg>
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-unm-primary02-400 tw-text-md tw-p-4 tw-pointer-events-none tw-bg-unm-bg-light">
          You currently have no letters.
        </div>
      )}
    </MultipleCardContainer>
  );
};
